import React, { useState, useEffect } from 'react';
import { X, Plus, Pencil, Trash2, Check } from 'lucide-react';
import { brandPackagesApi } from '../../api/modules/brandPackages';
import type { BrandPackage, BrandPackageColors } from '../../api/modules/brandPackages';
import { BrandPackagePreview } from './BrandPackagePreview';
import { BrandPackageEditor } from './BrandPackageEditor';
import type { GlobalPalette } from '../../utils/deriveComponentColors';

/**
 * Extract the palette from stored brand package colors
 * We store palette in _palette field when saving
 */
function extractPalette(colors: BrandPackageColors): GlobalPalette | undefined {
  const stored = colors as unknown as { _palette?: GlobalPalette };
  return stored._palette;
}

interface BrandPackageManagerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect?: (brandPackage: BrandPackage | null) => void;
  onBrandPackageEdited?: () => void;
  selectedBrandPackageId?: string | null;
  mode?: 'manage' | 'select'; // 'manage' for CRUD, 'select' for choosing
}

export const BrandPackageManager: React.FC<BrandPackageManagerProps> = ({
  isOpen,
  onClose,
  onSelect,
  onBrandPackageEdited,
  selectedBrandPackageId,
  mode = 'manage',
}) => {
  const [brandPackages, setBrandPackages] = useState<BrandPackage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingPackage, setEditingPackage] = useState<BrandPackage | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Load brand packages
  useEffect(() => {
    if (isOpen) {
      loadBrandPackages();
    }
  }, [isOpen]);

  const loadBrandPackages = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const packages = await brandPackagesApi.list();
      setBrandPackages(packages);
    } catch (err) {
      console.error('Failed to load brand packages:', err);
      setError('Failed to load brand packages');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this brand package?')) {
      return;
    }

    try {
      setDeletingId(id);
      await brandPackagesApi.delete(id);
      setBrandPackages(prev => prev.filter(bp => bp.id !== id));
      setError(null);
    } catch (err: any) {
      console.error('Failed to delete brand package:', err);

      // Check if this is a "brand package in use" error (409 Conflict)
      if (err?.status === 409 && err?.data?.message) {
        setError(err.data.message);
      } else {
        setError('Failed to delete brand package. Please try again.');
      }
    } finally {
      setDeletingId(null);
    }
  };

  const handleSave = async (savedPackage: BrandPackage) => {
    const wasEditing = !!editingPackage;

    setBrandPackages(prev => {
      const index = prev.findIndex(bp => bp.id === savedPackage.id);
      if (index >= 0) {
        const updated = [...prev];
        updated[index] = savedPackage;
        return updated;
      }
      return [savedPackage, ...prev];
    });
    setEditingPackage(null);
    setIsCreating(false);

    if (wasEditing && onBrandPackageEdited) {
      onBrandPackageEdited();
    }
  };

  const handleSelect = (brandPackage: BrandPackage) => {
    if (mode === 'select' && onSelect) {
      onSelect(brandPackage);
      onClose();
    }
  };

  const handleClearSelection = () => {
    if (mode === 'select' && onSelect) {
      onSelect(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  // Show editor if creating or editing
  if (isCreating || editingPackage) {
    return (
      <BrandPackageEditor
        brandPackage={editingPackage}
        onSave={handleSave}
        onCancel={() => {
          setEditingPackage(null);
          setIsCreating(false);
        }}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {mode === 'select' ? 'Select Brand Package' : 'Brand Packages'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {mode === 'select'
                  ? 'Choose a brand package to apply to your site'
                  : 'Create and manage your custom brand packages'}
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-red-600">{error}</p>
                <button
                  onClick={loadBrandPackages}
                  className="mt-4 text-blue-600 hover:text-blue-700"
                >
                  Try again
                </button>
              </div>
            ) : brandPackages.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 mb-4">You haven't created any brand packages yet.</p>
                <button
                  onClick={() => setIsCreating(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Brand Package
                </button>
              </div>
            ) : (
              <>
                {/* Error banner for delete failures */}
                {error && (
                  <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-amber-400"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3 flex-1">
                        <p className="text-sm text-amber-800">{error}</p>
                      </div>
                      <button
                        onClick={() => setError(null)}
                        className="ml-3 flex-shrink-0 text-amber-500 hover:text-amber-700"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {brandPackages.map(bp => (
                    <div
                      key={bp.id}
                      className={`relative border rounded-lg p-4 transition-all group ${
                        selectedBrandPackageId === bp.id
                          ? 'border-blue-500 ring-2 ring-blue-200'
                          : 'border-gray-200 hover:border-gray-300'
                      } ${mode === 'select' ? 'cursor-pointer' : ''}`}
                      onClick={() => mode === 'select' && handleSelect(bp)}
                    >
                      {/* Edit/Delete icons (top-left, visible on hover in select mode) */}
                      {mode === 'select' && (
                        <div className="absolute top-2 left-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              setEditingPackage(bp);
                            }}
                            className="p-1.5 bg-white border border-gray-300 rounded-md text-gray-600 hover:text-blue-600 hover:border-blue-300 shadow-sm"
                            title="Edit brand package"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              handleDelete(bp.id);
                            }}
                            disabled={deletingId === bp.id}
                            className="p-1.5 bg-white border border-gray-300 rounded-md text-gray-600 hover:text-red-600 hover:border-red-300 shadow-sm disabled:opacity-50"
                            title="Delete brand package"
                          >
                            {deletingId === bp.id ? (
                              <div className="animate-spin h-3.5 w-3.5 border-2 border-red-600 border-t-transparent rounded-full" />
                            ) : (
                              <Trash2 className="h-3.5 w-3.5" />
                            )}
                          </button>
                        </div>
                      )}

                      {/* Selected indicator */}
                      {selectedBrandPackageId === bp.id && (
                        <div className="absolute top-2 right-2 bg-blue-500 text-white rounded-full p-1">
                          <Check className="h-3 w-3" />
                        </div>
                      )}

                      {/* Preview */}
                      <BrandPackagePreview
                        colors={bp.colors}
                        fonts={bp.fonts}
                        palette={extractPalette(bp.colors)}
                        size="medium"
                      />

                      {/* Info */}
                      <div className="mt-3">
                        <h4 className="font-medium text-gray-900 truncate">{bp.name}</h4>
                        {bp.description && (
                          <p className="text-sm text-gray-500 truncate mt-1">{bp.description}</p>
                        )}
                      </div>

                      {/* Actions (only in manage mode) */}
                      {mode === 'manage' && (
                        <div className="mt-3 flex items-center gap-2">
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              setEditingPackage(bp);
                            }}
                            className="flex-1 inline-flex items-center justify-center px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                          >
                            <Pencil className="h-3.5 w-3.5 mr-1.5" />
                            Edit
                          </button>
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              handleDelete(bp.id);
                            }}
                            disabled={deletingId === bp.id}
                            className="px-3 py-1.5 text-sm border border-red-300 rounded-md text-red-700 hover:bg-red-50 disabled:opacity-50"
                          >
                            {deletingId === bp.id ? (
                              <div className="animate-spin h-3.5 w-3.5 border-2 border-red-600 border-t-transparent rounded-full" />
                            ) : (
                              <Trash2 className="h-3.5 w-3.5" />
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
            <div>
              {mode === 'select' && selectedBrandPackageId && (
                <button
                  onClick={handleClearSelection}
                  className="text-sm text-gray-600 hover:text-gray-800"
                >
                  Use theme colors instead
                </button>
              )}
            </div>
            <div className="flex gap-3">
              {brandPackages.length > 0 && (
                <button
                  onClick={() => setIsCreating(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Brand Package
                </button>
              )}
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {mode === 'select' ? 'Cancel' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandPackageManager;
